/**
 * Site Context Hook
 * Manages the current site and its status
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { LicenseStatus, ClaudeApiStatus } from '../lib/dashboard-api';
import { licenseApi, claudeApi } from '../lib/dashboard-api';

interface SiteState {
  isLoading: boolean;
  error: string | null;
  licenseStatus: LicenseStatus | null;
  claudeStatus: ClaudeApiStatus | null;
  isConfigured: boolean; // Has valid license AND Claude API
}

export function useSite() {
  const { currentSite, setCurrentSite } = useAuth();
  const [state, setState] = useState<SiteState>({
    isLoading: true,
    error: null,
    licenseStatus: null,
    claudeStatus: null,
    isConfigured: false,
  });

  /**
   * Fetch site status (license and Claude API)
   */
  const fetchSiteStatus = useCallback(async () => {
    if (!currentSite?.id) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        licenseStatus: null,
        claudeStatus: null,
        isConfigured: false,
      }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Fetch license and Claude status in parallel
      const [licenseResponse, claudeResponse] = await Promise.all([
        licenseApi.getStatus(currentSite.id).catch(() => ({ success: false, license: null })),
        claudeApi.getStatus(currentSite.id).catch(() => ({ success: false, status: null })),
      ]);

      const license = licenseResponse.success ? licenseResponse.license : null;
      const claude = claudeResponse.success ? claudeResponse.status : null;

      // Check if site is properly configured
      const hasValidLicense = license?.status === 'active';
      const hasValidClaude = claude?.status === 'valid';
      const isConfigured = hasValidLicense && hasValidClaude;

      setState({
        isLoading: false,
        error: null,
        licenseStatus: license,
        claudeStatus: claude,
        isConfigured,
      });

      // If the stored status is 'valid', silently re-verify against Anthropic in
      // the background. The DB status is cached and can go stale if Anthropic
      // revokes the key after it was saved. We never block the UI on this call.
      if (claude?.status === 'valid' && claude?.hasApiKey) {
        claudeApi.verify(currentSite.id)
          .then((result) => {
            if (result.valid === false) {
              setState(prev => ({
                ...prev,
                claudeStatus: prev.claudeStatus
                  ? { ...prev.claudeStatus, status: 'invalid' as ClaudeApiStatus['status'] }
                  : prev.claudeStatus,
                isConfigured: false,
              }));
            }
          })
          .catch(() => {
            // Network error — leave status unchanged
          });
      }
    } catch (err) {
      console.error('Failed to fetch site status:', err);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load site status',
      }));
    }
  }, [currentSite?.id]);

  // Fetch status when site changes
  useEffect(() => {
    fetchSiteStatus();
  }, [fetchSiteStatus]);

  /**
   * Activate license key
   */
  const activateLicense = useCallback(async (licenseKey: string) => {
    if (!currentSite?.id) throw new Error('No site selected');
    
    const response = await licenseApi.activate(currentSite.id, licenseKey);
    
    if (response.success) {
      // Refresh status
      await fetchSiteStatus();
    }
    
    return response;
  }, [currentSite?.id, fetchSiteStatus]);

  /**
   * Clear license
   */
  const clearLicense = useCallback(async () => {
    if (!currentSite?.id) throw new Error('No site selected');
    
    const response = await licenseApi.clear(currentSite.id);
    
    if (response.success) {
      await fetchSiteStatus();
    }
    
    return response;
  }, [currentSite?.id, fetchSiteStatus]);

  /**
   * Test Claude API key
   */
  const testClaudeKey = useCallback(async (apiKey: string) => {
    if (!currentSite?.id) throw new Error('No site selected');
    
    return claudeApi.test(currentSite.id, apiKey);
  }, [currentSite?.id]);

  /**
   * Save Claude API key.
   * Pass markValid: true when called after a confirmed successful test so the
   * backend stores claude_api_status = 'valid' immediately.
   */
  const saveClaudeKey = useCallback(async (apiKey: string, markValid = false) => {
    if (!currentSite?.id) throw new Error('No site selected');
    
    const response = await claudeApi.save(currentSite.id, apiKey, markValid);
    
    if (response.success) {
      await fetchSiteStatus();
    }
    
    return response;
  }, [currentSite?.id, fetchSiteStatus]);

  /**
   * Check if license tier is PRO
   */
  const isPro = state.licenseStatus?.tier?.toLowerCase().startsWith('pro') ?? false;

  return {
    currentSite,
    setCurrentSite,
    ...state,
    isPro,
    refreshStatus: fetchSiteStatus,
    activateLicense,
    clearLicense,
    testClaudeKey,
    saveClaudeKey,
  };
}

export default useSite;

